import type { JSX, ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isTokenExpired, getToken, getRefreshToken } from '../utils/tokenUtils';

// Function to check if the user is authenticated
const isAuthenticated = (): boolean => {
  const token = getToken(); // Automatically checks sessionStorage first, then localStorage
  const refreshToken = getRefreshToken(); // Automatically checks sessionStorage first, then localStorage

  // Allow access if:
  // 1. Token exists and is not expired, OR
  // 2. Token is expired but refresh token exists (axios interceptor will handle refresh)
  if (token && !isTokenExpired()) {
    return true; // Token is valid
  }
  
  // If token is expired but we have a refresh token, let axios interceptor handle it
  // This prevents redirect flashing during token refresh
  if (token && refreshToken) {
    return true; // Will be handled by axios interceptor on first API call
  }

  return false; // No tokens, truly not authenticated
};

// PrivateRoute component
const PrivateRoute = ({
  children,
}: {
  children: JSX.Element;
}): ReactElement => {
  const location = useLocation(); // Get the current location

  return isAuthenticated() ? (
    children
  ) : (
    // Navigate to the login page and pass the current location as state
    <Navigate to="/login" state={{ from: location }} />
  );
};

export default PrivateRoute;
